/**
 * JHP Locale utilities for Module Federation components
 *
 * When Zoomin components are federated to JHP, the host app's locale
 * needs to be synchronized with Zoomin's LocalizationManager.
 * This module provides utilities for that synchronization.
 */

import Language from '../localization/Language'
import Localization from '../localization/LocalizationManager'

/**
 * Map of locale strings to Language enum values.
 * Supports various formats: short codes, full names, BCP 47 tags.
 */
const localeToLanguageMap: Record<string, Language> = {
  // English
  en: Language.english,
  'en-US': Language.english,
  english: Language.english,
  English: Language.english,
  // Spanish
  es: Language.spanish,
  'es-ES': Language.spanish,
  spanish: Language.spanish,
  Spanish: Language.spanish,
  español: Language.spanish,
  Español: Language.spanish,
  // French
  fr: Language.french,
  'fr-FR': Language.french,
  french: Language.french,
  French: Language.french,
  français: Language.french,
  Français: Language.french,
  // Ukrainian
  uk: Language.ukrainian,
  'uk-UA': Language.ukrainian,
  ukrainian: Language.ukrainian,
  Ukrainian: Language.ukrainian,
  українська: Language.ukrainian,
  Українська: Language.ukrainian,
}

/**
 * Convert a locale string to a Zoomin Language enum value.
 * Returns undefined if the locale is not recognized.
 */
export function localeToLanguage(locale: string): Language | undefined {
  return localeToLanguageMap[locale]
}

/**
 * Set Zoomin's LocalizationManager language from a JHP locale string.
 * This is used by federated components to sync with the host app's locale.
 *
 * @param locale - Locale string from JHP (e.g., 'en', 'es', 'English', 'Español')
 */
export function setJHPLocale(locale: string | undefined): void {
  if (!locale) return

  const language = localeToLanguage(locale)
  if (language) {
    Localization.setLanguage(language)
  }
}
